import * as THREE from 'three'
import { applyAnimationEasing } from '@/plugins/animation/easing'
import type { EngineEventMap } from '@/runtime/EventBus'
import {
  CAMERA_DEFAULT_FAR,
  CAMERA_DEFAULT_NEAR,
  CAMERA_FAR_PADDING_RATIO,
  CAMERA_FIT_PADDING,
  CAMERA_MIN_DISTANCE,
  CAMERA_NEAR_BOUNDS_RATIO,
  CAMERA_NEAR_TARGET_RATIO,
  EPSILON,
  EPSILON_HIGH,
} from '@/shared/constants'
import type {
  CameraPatchOptions,
  CameraStatePatch,
  CameraTransitionOptions,
} from '@/shared/types/animation'
import type { CameraState } from '@/shared/types/core'
import { CameraMode } from '@/shared/types/core'
import type { CameraRig, FitOptions, ViewPreset } from '@/shared/types/services'

/** 安全方向向量：当 position 与 target 过近时的回退方向（Z-up 坐标系下沿 -Y 看） */
const SAFE_DIRECTION = new THREE.Vector3(0, -1, 0)

/** 世界 up 向量（Z-up 坐标系） */
const WORLD_UP = new THREE.Vector3(0, 0, 1)
const PRESET_POLE_OFFSET_RATIO = 1e-3

/** CameraRig 事件发射接口，用于广播相机状态变更 */
export interface CameraRigEventSink {
  /**
   * 发射相机状态变更事件。
   *
   * @param event - 事件名 'camera.changed'
   * @param data - 事件载荷，包含最新 CameraState
   */
  emit(event: 'camera.changed', data: EngineEventMap['camera.changed']): void
  /**
   * 发射相机模式变更事件。
   *
   * @param event - 事件名 'camera.modeChanged'
   * @param data - 事件载荷，包含新旧模式
   */
  emit(event: 'camera.modeChanged', data: EngineEventMap['camera.modeChanged']): void
}

/**
 * 相机装置实现：维护相机逻辑状态（position/target/up/fov/zoom/mode/near/far），
 * 通过 EventBus 广播状态变更，与 Three.js 相机实例解耦。
 */
export class CameraRigImpl implements CameraRig {
  private _position = new THREE.Vector3(5, -5, 7)
  private _target = new THREE.Vector3(0, 0, 0)
  private _up = new THREE.Vector3(0, 0, 1)
  private _fov = 60
  private _zoom = 1
  private _mode: CameraMode = CameraMode.Perspective
  private _near = CAMERA_DEFAULT_NEAR
  private _far = CAMERA_DEFAULT_FAR

  /** 当前动画的取消句柄 */
  private _animationFrameId = 0
  /** 当前动画的 reject 回调，用于取消时 resolve 旧 Promise */
  private _animationResolve: (() => void) | null = null
  private _isTransitioning = false

  private readonly _events: CameraRigEventSink

  constructor(events: CameraRigEventSink) {
    this._events = events
  }

  // ==================== Getters / Setters ====================

  /** 当前相机模式（Perspective/Orthographic） */
  get mode(): CameraMode {
    return this._mode
  }

  get isTransitioning(): boolean {
    return this._isTransitioning
  }

  /** 当前相机位置；getter 返回克隆副本 */
  get position(): THREE.Vector3 {
    return this._position.clone()
  }

  set position(value: THREE.Vector3) {
    if (this._isVectorEquivalent(this._position, value)) return
    this._position.copy(value)
    this._emitChanged()
  }

  /** 当前视线目标；getter 返回克隆副本 */
  get target(): THREE.Vector3 {
    return this._target.clone()
  }

  set target(value: THREE.Vector3) {
    if (this._isVectorEquivalent(this._target, value)) return
    this._target.copy(value)
    this._emitChanged()
  }

  /** up 向量只读，仅通过 setPresetView 或 setState 修改 */
  get up(): THREE.Vector3 {
    return this._up.clone()
  }

  /** 透视相机 FOV，单位度 */
  get fov(): number {
    return this._fov
  }

  set fov(value: number) {
    if (this._isNumberEquivalent(this._fov, value)) return
    this._fov = value
    this._emitChanged()
  }

  /** 正交相机缩放系数 */
  get zoom(): number {
    return this._zoom
  }

  set zoom(value: number) {
    if (this._isNumberEquivalent(this._zoom, value)) return
    this._zoom = value
    this._emitChanged()
  }

  /** 近裁剪距离，单位米 */
  get near(): number {
    return this._near
  }

  /** 远裁剪距离，单位米 */
  get far(): number {
    return this._far
  }

  /** 从当前 up 向量反算绕视线方向的 roll 角度（弧度） */
  get roll(): number {
    const dir = this._getViewDirection()
    // 计算零 roll 时的参考 up 向量
    const right = new THREE.Vector3().crossVectors(dir, WORLD_UP)
    if (right.lengthSq() < EPSILON_HIGH) {
      // 视线与世界 up 平行，退化情况下 roll 无意义
      return 0
    }
    right.normalize()
    const refUp = new THREE.Vector3().crossVectors(right, dir).normalize()
    // 用 atan2 计算当前 up 相对于 refUp 的旋转角
    const dot = this._up.dot(refUp)
    const cross = new THREE.Vector3().crossVectors(refUp, this._up)
    const sign = cross.dot(dir)
    return Math.atan2(sign, dot)
  }

  /** 设置绕视线方向的 roll 角度（弧度），更新 up 向量 */
  set roll(angle: number) {
    this._setRoll(angle)
    this._emitChanged()
  }

  private _setRoll(angle: number): void {
    const dir = this._getViewDirection()
    const right = new THREE.Vector3().crossVectors(dir, WORLD_UP)
    if (right.lengthSq() < EPSILON_HIGH) {
      // 视线与世界 up 平行，以 -Y 作为 right 的回退
      right.set(0, -1, 0)
    }
    right.normalize()
    const refUp = new THREE.Vector3().crossVectors(right, dir).normalize()
    // 将 refUp 绕 dir 旋转 angle 角度
    this._up.copy(refUp).applyAxisAngle(dir, angle).normalize()
  }

  // ==================== 公开方法 ====================

  /**
   * 切换相机投影模式，广播 modeChanged 和 changed 事件。
   *
   * @param mode - 目标相机模式
   */
  setMode(mode: CameraMode): void {
    if (mode === this._mode) return
    const previous = this._mode
    this._mode = mode
    this._events.emit('camera.modeChanged', { mode, previous })
    this._emitChanged()
  }

  /**
   * 将相机设置为俯视某个平面：position = origin + normal * distance，target = origin。
   *
   * @param origin - 平面原点
   * @param normal - 平面法线（将被归一化）
   * @param up - 参考 up 向量（将投影到平面上）
   * @param distance - 相机到平面的距离，单位米
   */
  setTopDownOnPlane(
    origin: THREE.Vector3,
    normal: THREE.Vector3,
    up: THREE.Vector3,
    distance: number
  ): void {
    const n = normal.clone().normalize()
    this._position.copy(origin).addScaledVector(n, distance)
    this._target.copy(origin)
    // 将 up 投影到平面上（去掉沿法线的分量），作为相机 up
    const projected = up.clone().addScaledVector(n, -up.dot(n))
    if (projected.lengthSq() < EPSILON_HIGH) {
      // up 与法线平行，退化时使用世界 Z 或 Y 作为回退
      projected.copy(Math.abs(n.z) < 0.9 ? WORLD_UP : new THREE.Vector3(0, 1, 0))
      projected.addScaledVector(n, -projected.dot(n))
    }
    this._up.copy(projected.normalize())
    this._emitChanged()
  }

  /**
   * 根据场景包围盒更新 near/far 裁剪面。
   *
   * @param sceneBounds - 场景包围盒；空包围盒将使用默认值
   */
  updateNearFar(sceneBounds: THREE.Box3): void {
    if (sceneBounds.isEmpty()) {
      const nextNear = CAMERA_DEFAULT_NEAR
      const nextFar = CAMERA_DEFAULT_FAR
      if (
        Math.abs(this._near - nextNear) <= EPSILON_HIGH &&
        Math.abs(this._far - nextFar) <= EPSILON_HIGH
      ) {
        return
      }
      this._near = nextNear
      this._far = nextFar
      this._emitChanged()
      return
    }

    const size = new THREE.Vector3()
    sceneBounds.getSize(size)
    const diagonal = size.length()

    if (this._mode === CameraMode.Orthographic) {
      const depthRange = this._getBoxDepthRange(sceneBounds)
      const padding = Math.max(1, diagonal * CAMERA_FAR_PADDING_RATIO)
      const nextNear = depthRange.min - padding
      const nextFar = Math.max(depthRange.max + padding, nextNear + 1)

      if (
        Math.abs(this._near - nextNear) <= EPSILON_HIGH &&
        Math.abs(this._far - nextFar) <= EPSILON_HIGH
      ) {
        return
      }

      this._near = nextNear
      this._far = nextFar
      this._emitChanged()
      return
    }

    const distanceToTarget = this._getDistanceToTarget()
    const closestPoint = sceneBounds.clampPoint(this._position, new THREE.Vector3())
    const distanceToBounds = this._position.distanceTo(closestPoint)
    const nearFromTarget = distanceToTarget * CAMERA_NEAR_TARGET_RATIO
    const nearFromBounds =
      distanceToBounds > 0 ? distanceToBounds * CAMERA_NEAR_BOUNDS_RATIO : nearFromTarget

    // near 优先跟随当前焦点与局部边界距离，避免大场景里按场景中心抬高 near。
    const nextNear = THREE.MathUtils.clamp(
      Math.min(nearFromTarget, nearFromBounds),
      0.01,
      Math.max(1, diagonal * CAMERA_FAR_PADDING_RATIO)
    )

    const maxCornerDistance = this._getMaxDistanceToBoxCorner(sceneBounds)
    const nextFar = Math.max(
      nextNear + 1,
      distanceToTarget + diagonal,
      maxCornerDistance + diagonal * CAMERA_FAR_PADDING_RATIO
    )

    if (
      Math.abs(this._near - nextNear) <= EPSILON_HIGH &&
      Math.abs(this._far - nextFar) <= EPSILON_HIGH
    ) {
      return
    }

    this._near = nextNear
    this._far = nextFar

    this._emitChanged()
  }

  /**
   * 适配相机视角使包围盒完整可见。
   *
   * @param box - 目标包围盒；空包围盒直接返回
   * @param options - 拟合选项，含 padding 等参数
   */
  fitToBox(box: THREE.Box3, options?: FitOptions): void {
    if (box.isEmpty()) return

    const padding = options?.padding ?? CAMERA_FIT_PADDING

    const center = new THREE.Vector3()
    box.getCenter(center)

    const size = new THREE.Vector3()
    box.getSize(size)
    const maxDim = Math.max(size.x, size.y, size.z)

    // 根据 fov 计算需要的相机距离
    const fovRad = THREE.MathUtils.degToRad(this._fov)
    const zoomFactor =
      this._mode === CameraMode.Orthographic ? Math.max(this._zoom, CAMERA_MIN_DISTANCE) : 1
    const fitDistance = (maxDim * padding * zoomFactor) / (2 * Math.tan(fovRad / 2))

    // 获取当前视线方向（从 position 到 target 的反方向）
    const direction = this._getViewDirection()
    direction.negate() // 从 target 指向 position 的方向

    this._target.copy(center)
    this._position.copy(center).addScaledVector(direction, fitDistance)

    this._emitChanged()
  }

  /**
   * 设置预设视角（前/后/左/右/顶/底/等轴等）。
   *
   * @param preset - 预设名称
   */
  setPresetView(preset: ViewPreset): void {
    const d = this._getDistanceToTarget()

    // target 保持当前值不变
    const t = this._target

    // Z-up 坐标系：Z=垂直, Y=前方, X=右方（与 Potree/LiDAR 数据一致）
    switch (preset) {
      case 'top':
        // 从上方往下看（-Z），screen-up = +Y
        {
          const lateral = Math.max(d * PRESET_POLE_OFFSET_RATIO, EPSILON)
          const vertical = Math.sqrt(Math.max(d * d - lateral * lateral, 0))
          this._position.set(t.x, t.y - lateral, t.z + vertical)
          this._up.copy(WORLD_UP)
        }
        break
      case 'bottom':
        // 从下方往上看（+Z），screen-up = +Y
        {
          const lateral = Math.max(d * PRESET_POLE_OFFSET_RATIO, EPSILON)
          const vertical = Math.sqrt(Math.max(d * d - lateral * lateral, 0))
          this._position.set(t.x, t.y + lateral, t.z - vertical)
          this._up.copy(WORLD_UP)
        }
        break
      case 'front':
        // 从前方看（沿 +Y 方向看），up = +Z
        this._position.set(t.x, t.y - d, t.z)
        this._up.set(0, 0, 1)
        break
      case 'back':
        // 从后方看（沿 -Y 方向看），up = +Z
        this._position.set(t.x, t.y + d, t.z)
        this._up.set(0, 0, 1)
        break
      case 'left':
        // 从左侧看（沿 +X 方向看），up = +Z
        this._position.set(t.x - d, t.y, t.z)
        this._up.set(0, 0, 1)
        break
      case 'right':
        // 从右侧看（沿 -X 方向看），up = +Z
        this._position.set(t.x + d, t.y, t.z)
        this._up.set(0, 0, 1)
        break
    }

    this._emitChanged()
  }

  /**
   * 动画过渡到目标状态，返回 Promise 在动画完成时 resolve。
   *
   * @param state - 目标状态增量，仅更新提供的字段
   * @param duration - 动画时长，单位毫秒，默认 500；≤0 立即应用
   * @returns 动画完成的 Promise
   */
  animateTo(
    state: Partial<CameraState>,
    duration: number | CameraTransitionOptions = 500,
    options?: CameraTransitionOptions
  ): Promise<void> {
    if (typeof duration !== 'number') {
      options = duration
      duration = options.duration ?? 500
    }
    // 取消正在进行的动画
    this._cancelAnimation()

    // duration <= 0 时直接设置
    if (duration <= 0) {
      this._applyPartialState(state)
      this._emitChanged()
      options?.onUpdate?.(this.getState(), {
        progress: 1,
        easedProgress: 1,
        completed: true,
      })
      return Promise.resolve()
    }

    return new Promise<void>((resolve) => {
      // 快照动画起始状态
      const startPos = this._position.clone()
      const startTarget = this._target.clone()
      const startUp = this._up.clone()
      const startFov = this._fov
      const startZoom = this._zoom

      // 目标状态（未指定的字段保持当前值）
      const endPos = state.position?.clone() ?? startPos.clone()
      const endTarget = state.target?.clone() ?? startTarget.clone()
      const endUp = state.up?.clone() ?? startUp.clone()
      const endFov = state.fov ?? startFov
      const endZoom = state.zoom ?? startZoom
      // 未显式传 easing 时保持旧行为（线性进度，不调用缓动函数）
      const easing = options?.easing

      const startTime = performance.now()

      this._animationResolve = resolve
      this._isTransitioning = true

      // mode 在动画启动时立即切换，不延迟到 rAF 回调
      if (state.mode !== undefined && state.mode !== this._mode) {
        this.setMode(state.mode)
      }

      const tick = (now: number): void => {
        const elapsed = now - startTime
        const t = Math.min(elapsed / duration, 1)

        const s = easing ? applyAnimationEasing(t, easing) : t

        this._position.lerpVectors(startPos, endPos, s)
        this._target.lerpVectors(startTarget, endTarget, s)
        this._up.lerpVectors(startUp, endUp, s).normalize()
        this._fov = startFov + (endFov - startFov) * s
        this._zoom = startZoom + (endZoom - startZoom) * s

        this._emitChanged()
        options?.onUpdate?.(this.getState(), {
          progress: t,
          easedProgress: s,
          completed: t >= 1,
        })

        if (t < 1) {
          this._animationFrameId = requestAnimationFrame(tick)
        } else {
          this._animationFrameId = 0
          this._animationResolve = null
          resolve()
          this._isTransitioning = false
        }
      }

      this._animationFrameId = requestAnimationFrame(tick)
    })
  }

  /**
   * 批量应用相机状态补丁，并确保最多只广播一次 camera.changed。
   *
   * @param patch - 只包含需要更新的相机字段
   * @param options - 应用选项
   */
  applyStatePatch(patch: CameraStatePatch, options?: CameraPatchOptions): void {
    if (options?.cancelTransition) {
      this._cancelAnimation()
    }
    const previousState = this.getState()
    this._applyPartialState(patch)
    if (!this._isStateEquivalent(previousState)) {
      this._emitChanged()
    }
  }

  /** 取消当前一次性相机过渡动画。 */
  cancelTransition(): void {
    this._cancelAnimation()
  }

  /** 是否存在正在执行的一次性相机过渡动画。 */
  /**
   * 返回当前相机状态的深拷贝。
   *
   * @returns CameraState 克隆副本
   */
  getState(): CameraState {
    return {
      position: this._position.clone(),
      target: this._target.clone(),
      up: this._up.clone(),
      fov: this._fov,
      zoom: this._zoom,
      mode: this._mode,
      roll: this.roll,
    }
  }

  /**
   * 设置完整相机状态（深拷贝输入值）。
   *
   * @param state - 新的 CameraState
   */
  setState(state: CameraState): void {
    const changed = !this._isStateEquivalent(state)
    this._cancelAnimation()
    if (!changed) return
    this._position.copy(state.position)
    this._target.copy(state.target)
    this._up.copy(state.up)
    this._fov = state.fov
    this._zoom = state.zoom
    if (state.mode !== this._mode) {
      const previous = this._mode
      this._mode = state.mode
      this._events.emit('camera.modeChanged', { mode: this._mode, previous })
    }
    // setState 已经显式提供了 up，roll 仅作为派生信息保留，避免在俯视/仰视奇异位姿下
    // 再次通过 roll 重建 up 时引入跳变。
    this._emitChanged()
  }

  /** 释放资源：取消动画，清理引用 */
  dispose(): void {
    this._cancelAnimation()
  }

  // ==================== 内部方法 ====================

  /** 广播 camera.changed 事件 */
  private _emitChanged(): void {
    this._events.emit('camera.changed', { state: this.getState() })
  }

  private _isStateEquivalent(state: CameraState): boolean {
    return (
      this._isVectorEquivalent(this._position, state.position) &&
      this._isVectorEquivalent(this._target, state.target) &&
      this._isVectorEquivalent(this._up, state.up) &&
      this._isNumberEquivalent(this._fov, state.fov) &&
      this._isNumberEquivalent(this._zoom, state.zoom) &&
      this._mode === state.mode
    )
  }

  private _isVectorEquivalent(a: THREE.Vector3, b: THREE.Vector3): boolean {
    return a.distanceToSquared(b) <= EPSILON_HIGH * EPSILON_HIGH
  }

  private _isNumberEquivalent(a: number, b: number): boolean {
    return Math.abs(a - b) <= EPSILON_HIGH
  }

  /** 取消正在进行的动画 */
  private _cancelAnimation(): void {
    this._isTransitioning = false
    if (this._animationFrameId !== 0) {
      cancelAnimationFrame(this._animationFrameId)
      this._animationFrameId = 0
    }
    if (this._animationResolve) {
      this._animationResolve()
      this._animationResolve = null
    }
  }

  /** 获取 position 到 target 的距离，保证最小值 */
  private _getDistanceToTarget(): number {
    return Math.max(this._position.distanceTo(this._target), CAMERA_MIN_DISTANCE)
  }

  /** 获取归一化的视线方向（从 position 指向 target），距离过近时使用安全方向 */
  private _getViewDirection(): THREE.Vector3 {
    const dir = new THREE.Vector3().subVectors(this._target, this._position)
    if (dir.lengthSq() < CAMERA_MIN_DISTANCE * CAMERA_MIN_DISTANCE) {
      return SAFE_DIRECTION.clone()
    }
    return dir.normalize()
  }

  /** 计算相机到包围盒最远角点的距离 */
  private _getMaxDistanceToBoxCorner(box: THREE.Box3): number {
    let maxDistance = 0
    const xs = [box.min.x, box.max.x]
    const ys = [box.min.y, box.max.y]
    const zs = [box.min.z, box.max.z]

    for (const x of xs) {
      for (const y of ys) {
        for (const z of zs) {
          const distance = this._position.distanceTo(new THREE.Vector3(x, y, z))
          maxDistance = Math.max(maxDistance, distance)
        }
      }
    }

    return maxDistance
  }

  private _getBoxDepthRange(box: THREE.Box3): { min: number; max: number } {
    const forward = this._getViewDirection()
    let min = Number.POSITIVE_INFINITY
    let max = Number.NEGATIVE_INFINITY
    const xs = [box.min.x, box.max.x]
    const ys = [box.min.y, box.max.y]
    const zs = [box.min.z, box.max.z]

    for (const x of xs) {
      for (const y of ys) {
        for (const z of zs) {
          const depth = new THREE.Vector3(x, y, z).sub(this._position).dot(forward)
          min = Math.min(min, depth)
          max = Math.max(max, depth)
        }
      }
    }

    return { min, max }
  }

  /** 将 Partial<CameraState> 应用到内部状态 */
  private _applyPartialState(state: Partial<CameraState>): void {
    if (state.position) this._position.copy(state.position)
    if (state.target) this._target.copy(state.target)
    if (state.up) this._up.copy(state.up)
    if (state.fov !== undefined) this._fov = state.fov
    if (state.zoom !== undefined) this._zoom = state.zoom
    if (state.mode !== undefined && state.mode !== this._mode) {
      const previous = this._mode
      this._mode = state.mode
      this._events.emit('camera.modeChanged', { mode: this._mode, previous })
    }
    if (state.roll !== undefined && !state.up) {
      this._setRoll(state.roll)
    }
  }
}

export default CameraRigImpl
